import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SharedQuestionsForOneCourse from '../molecules/SharedQuestionsForOneCourse';

const MasterSharedQuestions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [distinctCourses, setDistinctCourses] = useState([]);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Fetch shared questions and group by course
  useEffect(() => {
    const fetchSharedQuestions = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/sharing/library`);
        
        // Group questions by course (className + sharerName combination)
        const courseMap = response.data.reduce((acc, question) => {
          const courseKey = `${question.className}|||${question.sharerName}`;
          if (!acc[courseKey]) {
            acc[courseKey] = {
              className: question.className,
              sharerName: question.sharerName,
              sharedAt: question.sharedAt,
              moduleCount: new Set(),
              totalQuestions: 0
            };
          }
          acc[courseKey].moduleCount.add(question.questionModule);
          acc[courseKey].totalQuestions++;
          
          // Keep track of the most recent sharing date
          if (new Date(question.sharedAt) > new Date(acc[courseKey].sharedAt)) {
            acc[courseKey].sharedAt = question.sharedAt;
          }
          
          return acc;
        }, {});
        
        // Convert to array and add module count
        const coursesArray = Object.values(courseMap).map(course => ({
          ...course,
          moduleCount: course.moduleCount.size
        }));
        
        // Sort by most recent sharing date
        coursesArray.sort((a, b) => new Date(b.sharedAt) - new Date(a.sharedAt));
        
        setDistinctCourses(coursesArray);
      } catch (err) {
        console.error('Error fetching shared questions:', err);
        setError('Failed to load shared questions');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedQuestions();
  }, [apiUrl]);

  // Handle course selection
  const handleCourseSelect = (className, sharerName) => {
    setSelectedCourse({ className, sharerName });
  };

  // Handle back to course list
  const handleBackToCourses = () => {
    setSelectedCourse(null);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        color: 'var(--text-color-muted)'
      }}>
        Loading shared questions...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#dc2626',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
        border: '2px dashed #fecaca'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h3 style={{ marginBottom: '10px', color: '#dc2626' }}>Error Loading Questions</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (distinctCourses.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#666',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '2px dashed #dee2e6'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>📚</div>
        <h3 style={{ marginBottom: '10px', color: '#495057' }}>No Shared Questions</h3>
        <p>No questions have been shared to the library yet.</p>
      </div>
    );
  }

  // If a course is selected, show the SharedQuestionsForOneCourse component
  if (selectedCourse) {
    return (
      <SharedQuestionsForOneCourse
        className={selectedCourse.className}
        sharerName={selectedCourse.sharerName}
        onBack={handleBackToCourses}
      />
    );
  }

  // Show list of distinct courses
  return (
    <div style={{
      padding: '20px',
      fontFamily: 'var(--font-primary)',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: 'var(--standard-box-shadow)'
    }}>
      <div style={{
        marginBottom: '24px',
        textAlign: 'center'
      }}>
        <h2 style={{
          margin: '0 0 8px 0',
          fontSize: '24px',
          fontWeight: '600',
          color: 'var(--color1)'
        }}>
          Shared Question Library
        </h2>
        <p style={{
          margin: 0,
          color: 'var(--text-color-muted)',
          fontSize: '14px'
        }}>
          Below is a list of courses with shared groups of questions. Click on a course to browse the shared questions and, if you like, import them into your course.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gap: '12px' // Reduced from 16px
      }}>
        {distinctCourses.map((course, index) => (
          <div
            key={`${course.className}-${course.sharerName}`}
            onClick={() => handleCourseSelect(course.className, course.sharerName)}
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              backgroundColor: 'white',
              padding: '12px 16px', // Reduced from 20px
              cursor: 'pointer',
              boxShadow: 'var(--standard-box-shadow)', // Changed to use CSS variable
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color1-light)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--standard-box-shadow)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h4 style={{
                  margin: '0 0 6px 0', // Reduced from 8px
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--color2)' // Changed from --color1
                }}>
                  {course.className}
                </h4>
                <p style={{
                  margin: 0, // Removed bottom margin
                  fontSize: '14px',
                  color: 'var(--text-color-muted)', // Changed from --text-color
                  fontWeight: '400' // Changed from 500
                }}>
                  Shared by {course.sharerName} • {course.moduleCount} module{course.moduleCount !== 1 ? 's' : ''} • {course.totalQuestions} question{course.totalQuestions !== 1 ? 's' : ''}
                </p>
              </div>
              <div style={{
                fontSize: '18px',
                color: 'var(--color1)'
              }}>
                ▶
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MasterSharedQuestions;
